/**
 * In-app log ring buffer (Phase 3 - diagnostics).
 *
 * Patches the console once at boot to keep the last N formatted log lines in
 * memory, so a remote `log_capture` command can upload what the device has been
 * saying without any native logcat access.
 *
 * On the Android shell this is delivered ALONGSIDE the native logcat tail, not
 * replaced by it (see diagnostics.ts). The two are complementary and each holds
 * something the other cannot: the shell forwards only WARN/ERROR to logcat, so
 * everything at log/info/debug level exists only here - and this buffer lives in
 * the page, so a force-stop or a renderer crash erases it while logcat still has
 * the fatal trace. Neither is a superset.
 */

const MAX_LINES = 500;
const buffer: string[] = [];
let installed = false;

/**
 * 2026-09-25 diagnostics: a structured, per-line subscription on top of the
 * same console patch below - `logShipper.ts` uses this to durably ship a
 * filtered subset of what this buffer already captures, without changing
 * anything about the buffer itself (still 500 lines, still volatile, still
 * exactly what `log_capture` uploads today). Kept here rather than in the
 * shipper so there is only ONE console patch installed, not two racing to
 * wrap `console.*` first.
 */
export interface LogLine {
  ts: string;
  level: string;
  message: string;
}
const subscribers: Array<(line: LogLine) => void> = [];
export function onLogLine(fn: (line: LogLine) => void): () => void {
  subscribers.push(fn);
  return () => {
    const i = subscribers.indexOf(fn);
    if (i !== -1) subscribers.splice(i, 1);
  };
}

function format(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

/**
 * Samsung's own webapis wrappers `console.log` on EVERY call, and we poll
 * identity and network state every 30s: 18 lines of `ProductInfoManager_* cache:`
 * and `network.js ... message data` per half-minute. At 500 lines that is ~14
 * minutes - a capture taken after a black screen held nothing but that noise
 * (2026-10-12, HG43U800), the event itself long evicted. They are still sent to
 * the real console and to subscribers; only the ring buffer skips them.
 */
const POLLING_NOISE =
  /^\[ProductInfo\] ProductInfoManager_\w+ cache:|^\[network\.js\] (getIp|getActiveConnectionType) message data /;

export function isPollingNoise(message: string): boolean {
  return POLLING_NOISE.test(message);
}

function record(level: string, args: unknown[]): void {
  const ts = new Date().toISOString();
  const message = args.map(format).join(" ");
  if (!isPollingNoise(message)) {
    buffer.push(`${ts} [${level}] ${message}`);
    if (buffer.length > MAX_LINES) buffer.shift();
  }
  // Best-effort, synchronous, never lets a subscriber's own bug reach the
  // real console call this wraps - see installLogBuffer's own `original?.()`
  // immediately after this runs.
  for (const fn of subscribers) {
    try {
      fn({ ts, level, message });
    } catch {
      /* a subscriber's own failure must never break logging itself */
    }
  }
}

/** Install the console patch once. Safe to call repeatedly. */
export function installLogBuffer(): void {
  if (installed || typeof console === "undefined") return;
  installed = true;
  const levels = ["log", "info", "warn", "error", "debug"] as const;
  for (const level of levels) {
    const original = console[level]?.bind(console);
    console[level] = (...args: unknown[]) => {
      record(level, args);
      original?.(...args);
    };
  }
}

/** The captured log lines as a single newline-joined string (oldest first). */
export function getCapturedLogs(): string {
  return buffer.join("\n");
}
